@switch (cardState().kind) {
  @case ('fetching') {
    <div data-testid="invitation-card" class="flex flex-col gap-4 p-8 mx-8 mb-4 border border-border rounded-[8px]">
      <div class="grid grid-cols-[18px_minmax(0,1fr)] items-center gap-3 text-secondary">
        <app-icon name="calendar" size="s" class="text-gray-500" />
        <span class="text-variant-sm">Loading calendar event...</span>
      </div>
    </div>
  }
  @case ('live') {
    <div data-testid="invitation-card" class="flex flex-col gap-4 p-8 mx-8 mb-4 border border-border rounded-[8px]">
      <ng-container *ngTemplateOutlet="eventContext" />

      <div class="text-[14px] font-medium leading-[20px] text-gray-950">Are you attending?</div>

      <app-event-rsvp-buttons [event]="event()" />
    </div>
  }
  @case ('cancelled') {
    <div data-testid="invitation-card" class="flex flex-col gap-4 p-8 mx-8 mb-4 border border-border rounded-[8px]">
      <ng-container *ngTemplateOutlet="eventContext" />
      <stch-alert
        data-testid="cancelled-alert"
        type="error"
        message="This event has been cancelled"
        [customConfig]="{ title: '' }"
        class="block"
      />
    </div>
  }
  @case ('trashed') {
    <div data-testid="invitation-card" class="flex flex-col gap-4 p-8 mx-8 mb-4 border border-border rounded-[8px]">
      <ng-container *ngTemplateOutlet="eventContext" />
      <stch-alert
        data-testid="trashed-alert"
        type="error"
        message="This event was moved to trash"
        [customConfig]="{ title: '' }"
        class="block"
      />
    </div>
  }
  @case ('archived') {
    <div data-testid="invitation-card" class="flex flex-col gap-4 p-8 mx-8 mb-4 border border-border rounded-[8px]">
      <ng-container *ngTemplateOutlet="eventContext" />
      <stch-alert
        data-testid="archived-alert"
        type="error"
        message="This event was archived"
        [customConfig]="{ title: '' }"
        class="block"
      />
    </div>
  }
}

<!--
  Shared event-context block reused across live / cancelled / trashed / archived cases.
  Pulls the event from `activeEvent()` (non-null whenever any of those cases is active)
  so no `any`-typed template parameter is needed.
-->
<ng-template #eventContext>
  @if (activeEvent(); as event) {
    <h3 data-testid="event-title" class="text-gray-950">{{ event.title }}</h3>

    @if (event.startTime; as startTime) {
      <div class="grid grid-cols-[18px_minmax(0,1fr)] items-center gap-3 text-gray-950">
        <app-icon name="calendar" size="s" class="shrink-0 text-gray-500" />
        <!--
          An all-day event is a floating calendar date, not a moment, so it renders
          date-only and is never projected into a timezone.

          A timed event renders in the ACCOUNT (display) zone: this card only DISPLAYS an event it
          never edits, and display surfaces use the account zone. `event.timeZone` is the organizer's
          scheduled zone off the inbound ICS, and echoing it showed a New York account its invite in
          Kyrgyzstan time (SBUX-829). The zone rides as a pipe ARGUMENT rather than being left to
          `DateFormatService`'s default, because `DateFormatPipe` is pure — see `displayTimeZone`.
        -->
        <span data-testid="event-when" class="truncate text-[14px] leading-[20px]">
          @if (event.allDay) {
            {{ startTime | dateFormat: 'dateRange' : event.endTime }}
          } @else {
            {{ startTime | dateFormat: 'dateTimeRangeWithTz' : event.endTime : true : displayTimeZone() }}
          }
        </span>
      </div>
    }

    @if (event.meetingLink) {
      <div class="grid grid-cols-[18px_minmax(0,1fr)] items-center gap-3 text-gray-950">
        <app-icon name="link-bold" size="s" class="shrink-0 text-gray-500" />
        <app-link type="external" target="_blank" [href]="event.meetingLink" class="truncate">
          {{ event.meetingLink }}
        </app-link>
      </div>
    }

    @if (visibleParticipants().length > 0) {
      <div class="grid grid-cols-[18px_minmax(0,1fr)] items-center gap-3">
        <app-icon name="sapphire-groups" size="s" class="shrink-0 text-gray-500" />
        <div class="flex items-center gap-1.5 flex-wrap">
          @for (participant of visibleParticipants(); track participant.email) {
            <span
              class="inline-flex min-h-[18px] max-w-full items-center gap-1 rounded-[6px] bg-gray-100 px-2 text-[11px] leading-[18px] text-gray-700"
            >
              @if (participant.invitationAcceptStatus === InvitationAcceptStatus.ACCEPTED) {
                <app-icon name="sapphire-check" [size]="10" class="shrink-0 text-green-600" />
              } @else if (participant.invitationAcceptStatus === InvitationAcceptStatus.REJECTED) {
                <app-icon name="sapphire-close-small" [size]="10" class="shrink-0 text-red-600" />
              }
              <span class="break-words">{{ participant.displayName || participant.email }}</span>
            </span>
          }
        </div>
      </div>
    }

    <app-button2
      type="button"
      data-testid="open-calendar-event"
      appearance="primary-text"
      size="xs"
      class="w-fit"
      [disabled]="openingEvent()"
      (click)="openEventInCalendar()"
    >
      Open Event
    </app-button2>
  }
</ng-template>
